import { ApiError, ApiStreamInactivityError } from '../types/errors';
import { runtimeEnv } from '../config/runtimeEnv';
import { API_AUTHENTICATION_FAILED_EVENT } from './authEvents';

export interface ApiConfig {
  baseUrl: string;
  appToken: string;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  skipUserAuth?: boolean;
  retry?: boolean;
}

export type StreamEventHandler = (event: Record<string, unknown>) => void;
export type StreamPostOptions = { inactivityTimeoutMs?: number; skipUserAuth?: boolean };

const REQUEST_TIMEOUT_MS = 300_000;
const STREAM_OPEN_TIMEOUT_MS = 900_000;
const RETRY_DELAYS_MS = [400, 1_200];
const RETRYABLE_STATUS_CODES = new Set([408, 500, 502, 503, 504]);

function readResponseHeaders(response: Response): Record<string, string> {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key.toLowerCase()] = value;
  });
  return headers;
}

function isApiErrorPayload(value: unknown): value is { error?: unknown; message?: unknown } {
  return typeof value === 'object' && value !== null;
}

function readApiErrorMessage(
  errorData: unknown,
  errorText: string,
  response: Response,
): string {
  if (isApiErrorPayload(errorData) && typeof errorData.message === 'string') {
    return errorData.message;
  }

  if (isApiErrorPayload(errorData) && typeof errorData.error === 'string') {
    return errorData.error;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  return errorText || `API Error (${response.status}): ${response.statusText}`;
}

class ApiService {
  private config: ApiConfig;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(config: ApiConfig) {
    this.config = config;
  }

  private getDefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
    };
  }

  private refreshUserSession(): Promise<boolean> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefreshUserSession().finally(() => {
        this.refreshPromise = null;
      });
    }

    return this.refreshPromise;
  }

  private async performRefreshUserSession(): Promise<boolean> {
    try {
      const response = await this.fetchWithRetry(`${this.config.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.retryHttpOnlyRefresh();
        }

        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  private async retryHttpOnlyRefresh(): Promise<boolean> {
    const response = await this.fetchWithRetry(`${this.config.baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      return false;
    }

    return true;
  }

  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeoutMs = REQUEST_TIMEOUT_MS,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      return await fetch(url, {
        ...options,
        credentials: 'include',
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private shouldRetry(error: unknown, response?: Response): boolean {
    if (response) {
      return RETRYABLE_STATUS_CODES.has(response.status);
    }

    return error instanceof TypeError || (error instanceof Error && error.name === 'AbortError');
  }

  private notifyUserAuthenticationFailure(): void {
    window.dispatchEvent(new CustomEvent(API_AUTHENTICATION_FAILED_EVENT));
  }

  private async createApiErrorFromResponse(response: Response): Promise<ApiError> {
    const errorText = await response.text();
    let errorData: unknown = null;

    try {
      errorData = errorText ? JSON.parse(errorText) : null;
    } catch {
      errorData = null;
    }

    return new ApiError(
      readApiErrorMessage(errorData, errorText, response),
      response.status,
      response.statusText,
      errorData,
      readResponseHeaders(response),
    );
  }

  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    timeoutMs = REQUEST_TIMEOUT_MS,
  ): Promise<Response> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
      try {
        const response = await this.fetchWithTimeout(url, options, timeoutMs);

        if (!this.shouldRetry(undefined, response) || attempt === RETRY_DELAYS_MS.length) {
          return response;
        }
      } catch (error) {
        lastError = error;

        if (!this.shouldRetry(error) || attempt === RETRY_DELAYS_MS.length) {
          throw error;
        }
      }

      await this.delay(RETRY_DELAYS_MS[attempt]);
    }

    throw lastError;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      body,
      headers = {},
      skipUserAuth = false,
      retry = true,
    } = options;

    const url = `${this.config.baseUrl}${endpoint}`;
    const hasBody = body !== undefined && method !== 'GET';

    const requestHeaders = {
      ...headers,
    };

    if (hasBody && !Object.keys(requestHeaders).some((key) => key.toLowerCase() === 'content-type')) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    const fetchOptions: RequestInit = {
      method,
      headers: requestHeaders,
    };

    if (hasBody) {
      fetchOptions.body = JSON.stringify(body);
    }

    let response = retry
      ? await this.fetchWithRetry(url, fetchOptions)
      : await this.fetchWithTimeout(url, fetchOptions);

    if (response.status === 401 && !skipUserAuth) {
      const refreshed = await this.refreshUserSession();

      if (refreshed) {
        const retryOptions = {
          ...fetchOptions,
          headers: {
            ...requestHeaders,
          },
        };
        response = retry
          ? await this.fetchWithRetry(url, retryOptions)
          : await this.fetchWithTimeout(url, retryOptions);
      } else {
        this.notifyUserAuthenticationFailure();
      }
    }

    if (!response.ok) {
      if (response.status === 401 && !skipUserAuth) {
        this.notifyUserAuthenticationFailure();
      }

      throw await this.createApiErrorFromResponse(response);
    }

    const data = await response.json();
    return data;
  }

  async streamPost(
    endpoint: string,
    body: unknown,
    onEvent: StreamEventHandler,
    options: StreamPostOptions = {},
  ): Promise<void> {
    const response = await this.openStream(endpoint, body, options.skipUserAuth);
    if (!this.isNdjsonStream(response)) {
      await this.handleJsonStreamFallback(response, onEvent);
      return;
    }

    const reader = response.body?.getReader();

    if (!reader) {
      throw new ApiError('Streaming is not supported by this browser.', response.status, response.statusText);
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let inactivityTimer: number | undefined;
    let inactivityTimedOut = false;
    const resetInactivityTimer = () => {
      if (!options.inactivityTimeoutMs) {
        return;
      }
      if (inactivityTimer !== undefined) {
        window.clearTimeout(inactivityTimer);
      }
      inactivityTimer = window.setTimeout(() => {
        inactivityTimedOut = true;
        void reader.cancel().catch(() => undefined);
      }, options.inactivityTimeoutMs);
    };

    resetInactivityTimer();
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        resetInactivityTimer();
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          this.handleStreamLine(line, onEvent);
        }
      }
    } finally {
      if (inactivityTimer !== undefined) {
        window.clearTimeout(inactivityTimer);
      }
    }

    if (inactivityTimedOut) {
      throw new ApiStreamInactivityError();
    }
    const remaining = buffer + decoder.decode();
    this.handleStreamLine(remaining, onEvent);
  }

  private isNdjsonStream(response: Response): boolean {
    return (response.headers.get('Content-Type') || '').includes('application/x-ndjson');
  }

  private async handleJsonStreamFallback(
    response: Response,
    onEvent: StreamEventHandler,
  ): Promise<void> {
    const payload = await response.json() as Record<string, unknown>;
    onEvent({
      type: 'done',
      ...payload,
      analysis: payload.analysis ?? payload,
      answer: payload.answer ?? payload.response ?? '',
    });
  }

  private async openStream(endpoint: string, body: unknown, skipUserAuth = false): Promise<Response> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers: {
        ...this.getDefaultHeaders(),
        Accept: 'application/x-ndjson',
      },
      body: JSON.stringify(body || {}),
    };

    let response = await this.fetchWithTimeout(url, fetchOptions, STREAM_OPEN_TIMEOUT_MS);
    if (response.status === 401 && !skipUserAuth) {
      const refreshed = await this.refreshUserSession();
      if (refreshed) {
        response = await this.fetchWithTimeout(url, fetchOptions, STREAM_OPEN_TIMEOUT_MS);
      } else {
        this.notifyUserAuthenticationFailure();
      }
    }

    if (!response.ok) {
      throw await this.createApiErrorFromResponse(response);
    }

    return response;
  }

  private handleStreamLine(line: string, onEvent: StreamEventHandler): void {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }

    const event = JSON.parse(trimmed) as Record<string, unknown>;
    if (event.type === 'error') {
      throw new ApiError(String(event.message || 'Streaming request failed.'), 500, 'Stream Error', event);
    }
    onEvent(event);
  }

  async get<T>(endpoint: string, headers?: Record<string, string>, skipUserAuth?: boolean): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers, skipUserAuth });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>,
    skipUserAuth?: boolean,
    retry?: boolean,
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, headers, skipUserAuth, retry });
  }

  async put<T>(endpoint: string, body?: unknown, headers?: Record<string, string>, skipUserAuth?: boolean): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers, skipUserAuth });
  }

  async patch<T>(endpoint: string, body?: unknown, headers?: Record<string, string>, skipUserAuth?: boolean): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, headers, skipUserAuth });
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>, skipUserAuth?: boolean): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers, skipUserAuth });
  }

  updateConfig(config: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getBaseUrl(): string {
    return this.config.baseUrl;
  }
}

export function resolveApiBaseUrl(configuredBaseUrl: string | undefined): string {
  return configuredBaseUrl || '/api';
}

const defaultConfig: ApiConfig = {
  baseUrl: resolveApiBaseUrl(runtimeEnv.VITE_BACKEND_PROXY_URL),
  appToken: '',
};

export const apiService = new ApiService(defaultConfig);

export interface RateMessagePayload {
  id: string;
  rate: number;
  comment?: string;
}

export async function rateMessage(
  messageId: string,
  rating: number,
  comment?: string
): Promise<void> {
  const payload: RateMessagePayload = {
    id: messageId,
    rate: rating,
    comment
  };

  await apiService.post('/bot/rate', payload);
}

export default apiService;
